/**
 * @fileoverview Event Filters Bar component.
 * @summary Displays filter buttons for event status filtering.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/components/EventFiltersBar
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSquareCheck, faSquare } from '@fortawesome/free-regular-svg-icons'
import type { EventStatus } from '../../../store/slices/eventsSlice'

interface EventFiltersBarProps {
  currentFilter: EventStatus | 'all'
  onFilterChange: (status: EventStatus | 'all') => void
  includeHidden?: boolean
  onIncludeHiddenChange?: (include: boolean) => void
}

/**
 * @summary Event filters bar component.
 * @param {EventFiltersBarProps} props - Component props.
 * @returns {React.ReactElement} Filters bar.
 */
export function EventFiltersBar({
  currentFilter,
  onFilterChange,
  includeHidden = false,
  onIncludeHiddenChange,
}: EventFiltersBarProps) {
  const { t } = useTranslation()

  const filters: Array<{ value: EventStatus | 'all'; label: string }> = [
    { value: 'all', label: t('dashboard.filterAll') },
    { value: 'draft', label: t('dashboard.filterDraft') },
    { value: 'active', label: t('dashboard.filterActive') },
    { value: 'completed', label: t('dashboard.filterCompleted') },
    { value: 'cancelled', label: t('dashboard.filterCancelled') },
  ]

  return (
    <div className="mb-4 flex flex-wrap justify-between gap-2 items-center">
      {filters.map((filter) => (
        <button
          key={filter.value}
          type="button"
          onClick={() => onFilterChange(filter.value)}
          className={`flex-1 btn ${currentFilter === filter.value ? 'btn-primary' : 'btn-secondary'} btn-sm`}
        >
          {filter.label}
        </button>
      ))}
      {onIncludeHiddenChange && (
        <button
          type="button"
          onClick={() => onIncludeHiddenChange(!includeHidden)}
          className={`flex-1 btn btn-sm  ${includeHidden ? 'btn-warning' : 'btn-accent'}`}
          title={t('events.showHidden') || 'Afficher les événements masqués'}
        >
          <FontAwesomeIcon 
            icon={includeHidden ? faSquareCheck : faSquare} 
            className="w-4 h-4 mr-1" 
          />
          {t('events.showHidden') || 'Masqués'}
        </button>
      )}
    </div>
  )
}

